import React, { useEffect, useState } from 'react';
import { EChartsOption } from 'echarts';
import BaseChart from './BaseChart';
import { artistAnalyticsService } from '../../services/api/artist-analytics.service';
import type { TotalPlaylistReachData } from '../../services/api/artist-analytics.service';
import {
  getCategoryAxisLayout,
  maxGridBottomPct,
  splitWordsOnSpace,
} from '../../utils/echartsCategoryAxis';
import { CHART_NO_DATA_FOR_ARTIST } from './chartMessages';

interface TotalPlaylistMixedChartProps {
  platformArtistId?: string;
  artistId?: string;
  title?: string;
  className?: string;
  refreshKey?: number;
}

const TotalPlaylistMixedChart: React.FC<TotalPlaylistMixedChartProps> = ({
  platformArtistId,
  artistId,
  title = 'Total Playlist Count',
  className = '',
  refreshKey = 0
}) => {
  const [data, setData] = useState<TotalPlaylistReachData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await artistAnalyticsService.getTotalPlaylistReach(platformArtistId);
        setData(response.data);
      } catch (err) {
        setError('Failed to load playlist count data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [artistId, platformArtistId, refreshKey]);

  const formatLargeNumber = (num: number): string => {
    if (num >= 1000000000000) {
      return `${(num / 1000000000000).toFixed(1)}T`;
    } else if (num >= 1000000000) {
      return `${(num / 1000000000).toFixed(1)}B`;
    } else if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  const getChartOption = (): EChartsOption => {
    if (!data || data.length === 0) return {};

    // Platform colors
    const platformColors: { [key: string]: string } = {
      'Spotify': '#1DB954',
      'Apple Music': '#FA233B',
      'YouTube Music': '#FF0000',
      'Deezer': '#FF6600',
      'Amazon Music': '#FF9900',
      'Pandora': '#005583',
      'SoundCloud': '#FF5500'
    };

    // Sort by total playlists descending (since we're focusing on count)
    const sortedData = [...data].sort((a, b) => b.total_playlists - a.total_playlists);

    // Filter to only show Spotify, Apple Music, and YouTube Music
    const allowedPlatforms = ['Spotify', 'Apple Music', 'YouTube Music'];
    const filteredData = sortedData.filter(item => 
      allowedPlatforms.includes(item.platform_name)
    );

    const platforms = filteredData.map(item => item.platform_name);
    const totalReachData = filteredData.map(item => item.total_reach);
    const totalPlaylistsData = filteredData.map(item => item.total_playlists);
    const xLayout = getCategoryAxisLayout(platforms.length);

    // Use first platform's color for bar legend (Spotify green)
    const firstPlatformColor = filteredData.length > 0 
      ? platformColors[filteredData[0].platform_name] || '#6366f1'
      : '#6366f1';

    return {
      backgroundColor: '#ffffff',
      color: [firstPlatformColor, '#F59E0B'], // Set explicit colors for series and legend (first platform color for bars, orange for line)
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999'
          }
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        borderRadius: 8,
        textStyle: {
          color: '#1f2937',
          fontSize: 12
        },
        hideDelay: 0,
        enterable: false,
        formatter: function(params: any) {
          const dataIndex = params[0].dataIndex;
          const dataItem = filteredData[dataIndex];
          
          return `
            <div style="font-weight: 600; margin-bottom: 8px; color: #fff;">${dataItem.platform_name}</div>
            <div style="color: ${platformColors[dataItem.platform_name] || '#6366f1'}; margin-bottom: 4px;">
              ● Total Reach: ${formatLargeNumber(dataItem.total_reach)}
            </div>
            <div style="color: #F59E0B; margin-bottom: 4px;">
              ● Total Playlists: ${formatLargeNumber(dataItem.total_playlists)}
            </div>
            <div style="color: #9ca3af; font-size: 11px;">
              Avg Reach per Track: ${formatLargeNumber(dataItem.avg_reach_per_track)}
            </div>
          `;
        }
      },
      legend: {
        data: ['Total Reach', 'Total Playlists'],
        textStyle: {
          color: '#6b7280',
          fontSize: 11
        },
        top: 5,
        right: 20
      },
      grid: {
        left: '10%',
        right: '10%',
        top: '20%',
        bottom: maxGridBottomPct('25%', xLayout.gridBottom),
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: platforms,
        axisPointer: {
          type: 'shadow'
        },
        axisLine: {
          lineStyle: {
            color: '#d1d5db'
          }
        },
        axisLabel: {
          color: '#6b7280',
          fontSize: xLayout.fontSize,
          rotate: xLayout.rotate,
          interval: 0,
          lineHeight: 13,
          formatter: (value: string) => splitWordsOnSpace(value, 11)
        },
        axisTick: {
          show: false
        }
      },
      yAxis: [
        {
          type: 'value',
          name: 'Total Reach',
          position: 'left',
          alignTicks: true,
          nameTextStyle: {
            color: '#6b7280',
            fontSize: 10
          },
          axisLine: {
            show: false
          },
          axisLabel: {
            color: '#6b7280',
            fontSize: 10,
            formatter: function(value: number) {
              return formatLargeNumber(value);
            }
          },
          splitLine: {
            lineStyle: {
              color: '#d1d5db',
              width: 1
            }
          }
        },
        {
          type: 'value',
          name: 'Total Playlists',
          position: 'right',
          alignTicks: true,
          nameTextStyle: {
            color: '#F59E0B',
            fontSize: 10
          },
          axisLine: {
            show: false
          },
          axisLabel: {
            color: '#F59E0B',
            fontSize: 10,
            formatter: function(value: number) {
              return formatLargeNumber(value);
            }
          },
          splitLine: {
            show: false
          }
        }
      ],
      series: [
        {
          name: 'Total Reach',
          type: 'bar',
          yAxisIndex: 0,
          color: firstPlatformColor, // Set color at series level for legend (uses first platform's color)
          data: filteredData.map((item) => ({
            value: item.total_reach,
            itemStyle: {
              color: platformColors[item.platform_name] || '#6366f1',
              borderRadius: [4, 4, 0, 0]
            }
          })),
          barWidth: '60%',
          emphasis: {
            itemStyle: {
              shadowBlur: 8,
              shadowColor: 'rgba(0, 0, 0, 0.3)'
            }
          },
          animationDelay: function (idx: number) {
            return idx * 100;
          }
        },
        {
          name: 'Total Playlists',
          type: 'line',
          yAxisIndex: 1,
          color: '#F59E0B', // Set color at series level for legend
          data: totalPlaylistsData,
          lineStyle: {
            color: '#F59E0B',
            width: 3
          },
          itemStyle: {
            color: '#F59E0B',
            borderWidth: 2,
            borderColor: '#1C1C1C'
          },
          symbol: 'circle',
          symbolSize: 8,
          emphasis: {
            itemStyle: {
              shadowBlur: 8,
              shadowColor: 'rgba(245, 158, 11, 0.5)'
            }
          },
          animationDelay: function (idx: number) {
            return idx * 100 + 500;
          }
        }
      ]
    };
  };

  if (loading) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <p className="text-red-400 text-xs">{CHART_NO_DATA_FOR_ARTIST}</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-transparent px-2 pt-2 pb-1">
      <BaseChart
        option={getChartOption()}
        loading={loading}
        className="w-full h-full"
      />
    </div>
  );
};

export default TotalPlaylistMixedChart;
